import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Anchor, Button, Card, Checkbox, Code, CopyButton, Group, Loader, PasswordInput, Stack, Text, Title } from '@mantine/core'
import { IconAlertCircle, IconBrandGoogleDrive, IconCheck, IconCopy, IconDownload, IconLock, IconShieldLock } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiSend } from '../api/client'
import { can, fetchSignInMethods } from '../api/session'
import { sessionQueryOptions } from '../session-store'
import {
  lockDataKeyTo,
  newDataKey,
  newKeyPair,
  newRecoveryCode,
  normaliseRecoveryCode,
  openDataKeyWithCode,
  sealDataKeyWithCode,
  suggestPassword,
  unlockDataKey,
  unlockPrivateKey,
  type Sealed,
} from './crypto'
import { FinanceStore, financeUnlocked, setFinanceStore } from './store'
import { FINANCE_QUERY_ROOTS } from './queries'

/**
 * Finance opens only to a password, and only in this browser.
 *
 * The gate sits in front of every finance screen. Until it is open there is no
 * data key in memory and nothing to decrypt with, whatever the server sends.
 * It closes on its own after half an hour without a click or a key, and on the
 * Lock button; closing throws away the key and every decrypted query.
 */

type KeyView = {
  public_key: string | null
  wrapped_private_key: Sealed | null
  wrapped_data_key: string | null
  wrapped_data_key_nonce: string | null
  salt: string
  iterations: number
}
type KeyState = { initialised: boolean; own: KeyView | null; recovery: KeyView | null }

/** The choices offered for locking after inactivity, in minutes. */
export const IDLE_CHOICES = [1, 5, 15, 30, 60] as const
const DEFAULT_IDLE_MINUTES = 15
const IDLE_KEY = 'medulla.finance.idle-minutes'

/** A per-browser preference: how long finance may sit untouched before it locks. */
function storedIdleMinutes(): number {
  try {
    const value = Number(window.localStorage.getItem(IDLE_KEY))

    return (IDLE_CHOICES as readonly number[]).includes(value) ? value : DEFAULT_IDLE_MINUTES
  } catch {
    return DEFAULT_IDLE_MINUTES
  }
}
const MIN_PASSWORD = 12

type Vault = {
  unlocked: boolean
  lock: () => void
  open: (store: FinanceStore) => void
  idleMinutes: number
  setIdleMinutes: (minutes: number) => void
  /** Why it last locked, when it was not somebody pressing Lock: shown on the unlock screen. */
  lockedFor: string | null
}
const VaultContext = createContext<Vault>({ unlocked: false, lock: () => undefined, open: () => undefined, idleMinutes: DEFAULT_IDLE_MINUTES, setIdleMinutes: () => undefined, lockedFor: null })

export function useVault(): Vault {
  return useContext(VaultContext)
}

export function VaultProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [unlocked, setUnlocked] = useState(financeUnlocked())
  const [idleMinutes, setIdle] = useState(storedIdleMinutes)
  const [lockedFor, setLockedFor] = useState<string | null>(null)

  const shut = useCallback(
    (reason: string | null) => {
      setFinanceStore(null)
      setUnlocked(false)
      setLockedFor(reason)
      for (const root of FINANCE_QUERY_ROOTS) queryClient.removeQueries({ queryKey: [root] })
    },
    [queryClient],
  )

  const lock = useCallback(() => shut(null), [shut])

  const openStore = useCallback((store: FinanceStore) => {
    setFinanceStore(store)
    setUnlocked(true)
    setLockedFor(null)
  }, [])

  const setIdleMinutes = useCallback((minutes: number) => {
    setIdle(minutes)
    try {
      window.localStorage.setItem(IDLE_KEY, String(minutes))
    } catch {
      // A private window: the choice lasts for this visit.
    }
  }, [])

  /*
   * Locks itself after the chosen time without a click, a key or a scroll.
   *
   * Measured against the clock rather than left to one timer: a browser slows
   * timers in a background tab and stops them while a laptop sleeps, so a
   * five-minute lock could fire hours late. The check runs every few seconds
   * and the moment the tab is looked at again.
   */
  useEffect(() => {
    if (!unlocked) return
    const limit = idleMinutes * 60_000
    let last = Date.now()
    const touch = () => {
      last = Date.now()
    }
    const check = () => {
      if (Date.now() - last >= limit) shut(`Locked after ${idleMinutes === 1 ? '1 minute' : `${idleMinutes} minutes`} without activity.`)
    }
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    events.forEach((name) => window.addEventListener(name, touch, { passive: true }))
    document.addEventListener('visibilitychange', check)
    const interval = window.setInterval(check, 5_000)

    return () => {
      events.forEach((name) => window.removeEventListener(name, touch))
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(interval)
    }
  }, [unlocked, idleMinutes, shut])

  const value = useMemo(() => ({ unlocked, lock, open: openStore, idleMinutes, setIdleMinutes, lockedFor }), [unlocked, lock, openStore, idleMinutes, setIdleMinutes, lockedFor])

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>
}

/** What stands in front of a finance screen until finance is unlocked. */
export function FinanceGate({ children }: { children: ReactNode }) {
  const { unlocked } = useVault()
  const { data: user } = useQuery(sessionQueryOptions)
  const keys = useQuery({ queryKey: ['finance-keys'], queryFn: ({ signal }) => apiGet<KeyState>('/finance/key', { signal }), enabled: !unlocked && can(user, 'finance.read'), staleTime: 0, gcTime: 0 })

  if (unlocked) return <>{children}</>

  if (!can(user, 'finance.read')) {
    return (
      <Panel title="Finance">
        <Text size="sm" c="dimmed">
          You do not have access to finance.
        </Text>
      </Panel>
    )
  }

  if (keys.isPending) return <Loader size="sm" m="xl" />
  if (keys.isError) return <Panel title="Finance">{failure(keys.error)}</Panel>

  const state = keys.data

  if (!state.initialised) {
    return can(user, 'finance.access.manage') ? (
      <SetUp onDone={() => void keys.refetch()} />
    ) : (
      <Panel title="Finance is not set up yet">
        <Text size="sm" c="dimmed">
          Somebody who looks after finance has to set it up first.
        </Text>
      </Panel>
    )
  }

  if (!state.own) return <SetOwnPassword recovery={state.recovery} onDone={() => void keys.refetch()} />

  if (!state.own.wrapped_data_key) {
    return (
      <Panel title="Waiting for access">
        <Text size="sm" c="dimmed">
          Your finance password is set. Somebody who already has access has to give you the key from Finance access, then this opens.
        </Text>
        <Group>
          <Button variant="light" onClick={() => void keys.refetch()}>
            Check again
          </Button>
        </Group>
        <RecoverWithCode recovery={state.recovery} onDone={() => void keys.refetch()} label="I have the recovery code" />
      </Panel>
    )
  }

  return <Unlock own={state.own} recovery={state.recovery} />
}

function Unlock({ own, recovery }: { own: KeyView; recovery: KeyView | null }) {
  const { open, lockedFor } = useVault()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recovering, setRecovering] = useState(false)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const privateKey = await unlockPrivateKey(password, own.wrapped_private_key!, own.salt, own.iterations)
      open(new FinanceStore(await unlockDataKey(privateKey, own.wrapped_data_key!)))
    } catch {
      setError('That is not your finance password.')
    } finally {
      setBusy(false)
    }
  }

  if (recovering) return <RecoverWithCode recovery={recovery} onDone={() => undefined} onCancel={() => setRecovering(false)} />

  return (
    <Panel title="Unlock finance" icon={<IconLock size={18} />}>
      {lockedFor && (
        <Text size="sm" c="dimmed">
          {lockedFor}
        </Text>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <Stack gap="sm">
          <PasswordInput label="Finance password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoFocus error={error} autoComplete="current-password" />
          <Group justify="space-between">
            <Anchor component="button" type="button" size="sm" onClick={() => setRecovering(true)}>
              Forgot it? Use the recovery code
            </Anchor>
            <Button type="submit" loading={busy} disabled={password === ''}>
              Unlock
            </Button>
          </Group>
        </Stack>
      </form>
    </Panel>
  )
}

/**
 * The recovery code opens the recovery copy of the data key; a new password
 * then gets a new key pair with the data key already locked to it, so the
 * person is back in without anybody else's help.
 */
function RecoverWithCode({ recovery, onDone, onCancel, label }: { recovery: KeyView | null; onDone: () => void; onCancel?: () => void; label?: string }) {
  const { open } = useVault()
  const [shown, setShown] = useState(!label)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!shown) {
    return (
      <Anchor component="button" type="button" size="sm" onClick={() => setShown(true)}>
        {label}
      </Anchor>
    )
  }

  const submit = async () => {
    if (!recovery?.wrapped_data_key || !recovery.wrapped_data_key_nonce) return setError('There is no recovery key on record.')
    if (password.length < MIN_PASSWORD) return setError(`The new password needs at least ${MIN_PASSWORD} characters.`)
    if (password !== confirm) return setError('The two passwords are not the same.')
    setBusy(true)
    setError(null)
    try {
      const dataKey = await openDataKeyWithCode(code, { nonce: recovery.wrapped_data_key_nonce, data: recovery.wrapped_data_key }, recovery.salt, recovery.iterations).catch(() => {
        throw new Error('That recovery code does not open finance.')
      })
      const pair = await newKeyPair(password)
      await apiSend('/finance/key/me', 'PUT', {
        public_key: pair.publicKey,
        wrapped_private_key: pair.wrappedPrivateKey,
        salt: pair.salt,
        iterations: pair.iterations,
        wrapped_data_key: await lockDataKeyTo(dataKey, pair.publicKey),
      })
      open(new FinanceStore(dataKey))
      onDone()
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Use the recovery code" icon={<IconShieldLock size={18} />}>
      <Text size="sm" c="dimmed">
        The code you saved when finance was set up, and a new finance password to use from now on.
      </Text>
      <PasswordInput label="Recovery code" value={code} onChange={(event) => setCode(normaliseRecoveryCode(event.currentTarget.value))} visible />
      <PasswordInput label="New finance password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoComplete="new-password" />
      <PasswordInput label="New finance password again" value={confirm} onChange={(event) => setConfirm(event.currentTarget.value)} autoComplete="new-password" error={error} />
      <Group justify="flex-end">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Back
          </Button>
        )}
        <Button loading={busy} onClick={() => void submit()} disabled={code === '' || password === ''}>
          Unlock and set password
        </Button>
      </Group>
    </Panel>
  )
}

/** Somebody given the finance permission sets their own password, then waits to be given the key. */
function SetOwnPassword({ recovery, onDone }: { recovery: KeyView | null; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (password.length < MIN_PASSWORD) return setError(`At least ${MIN_PASSWORD} characters.`)
    if (password !== confirm) return setError('The two passwords are not the same.')
    setBusy(true)
    try {
      const pair = await newKeyPair(password)
      await apiSend('/finance/key/me', 'PUT', { public_key: pair.publicKey, wrapped_private_key: pair.wrappedPrivateKey, salt: pair.salt, iterations: pair.iterations })
      onDone()
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Set your finance password" icon={<IconShieldLock size={18} />}>
      <Text size="sm" c="dimmed">
        Finance is encrypted, and this password is what opens it for you. It is never sent anywhere and cannot be reset by anybody, so keep it somewhere safe.
      </Text>
      <PasswordInput label="Finance password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoComplete="new-password" />
      <PasswordInput label="Finance password again" value={confirm} onChange={(event) => setConfirm(event.currentTarget.value)} autoComplete="new-password" error={error} />
      <Group justify="flex-end">
        <Button loading={busy} onClick={() => void submit()}>
          Set password
        </Button>
      </Group>
      <RecoverWithCode recovery={recovery} onDone={onDone} label="I have the recovery code" />
    </Panel>
  )
}

/**
 * The first time: a password, a recovery code saved somewhere that is not
 * this computer, and the keys made from both. Nothing is stored until the
 * code has been saved, because a recovery code nobody kept recovers nothing.
 */
function SetUp({ onDone }: { onDone: () => void }) {
  const { open } = useVault()
  const signIn = useQuery({ queryKey: ['sign-in-methods'], queryFn: ({ signal }) => fetchSignInMethods(signal) })
  const code = useRef(newRecoveryCode()).current
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [includePassword, setIncludePassword] = useState(true)
  const [saved, setSaved] = useState(false)
  const [savedTo, setSavedTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = password.length >= MIN_PASSWORD && password === confirm
  const file = recoveryText(code, includePassword ? password : null)

  const finish = async () => {
    setBusy(true)
    setError(null)
    try {
      const dataKey = await newDataKey()
      const pair = await newKeyPair(password)
      const recovery = await sealDataKeyWithCode(dataKey, code)
      await apiSend('/finance/key/setup', 'POST', {
        public_key: pair.publicKey,
        wrapped_private_key: pair.wrappedPrivateKey,
        salt: pair.salt,
        iterations: pair.iterations,
        wrapped_data_key: await lockDataKeyTo(dataKey, pair.publicKey),
        recovery: { wrapped_data_key: recovery.wrapped, salt: recovery.salt, iterations: recovery.iterations },
      })

      const store = new FinanceStore(dataKey)
      // The usual Indian taxes, so the first invoice has something to pick.
      for (const tax of [
        { name: 'CGST', rate_basis_points: 900 },
        { name: 'SGST', rate_basis_points: 900 },
        { name: 'IGST', rate_basis_points: 1800 },
      ]) {
        await store.save('tax', crypto.randomUUID(), tax, 0)
      }
      open(store)
      onDone()
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Setting finance up did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Set up finance" icon={<IconShieldLock size={18} />}>
      <Text size="sm" c="dimmed">
        Everything in finance is encrypted in your browser before it is saved. Nobody can read it without a finance password or the recovery code, including whoever runs the server.
      </Text>

      <Stack gap={6}>
        <Text fw={600} size="sm">
          1. Your finance password
        </Text>
        <Group align="flex-end" gap="xs" wrap="nowrap">
          <PasswordInput style={{ flex: 1 }} label="Finance password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoComplete="new-password" />
          <Button
            variant="default"
            onClick={() => {
              const suggested = suggestPassword()
              setPassword(suggested)
              setConfirm(suggested)
            }}
          >
            Suggest one
          </Button>
        </Group>
        <PasswordInput label="Finance password again" value={confirm} onChange={(event) => setConfirm(event.currentTarget.value)} autoComplete="new-password" error={confirm && password !== confirm ? 'The two passwords are not the same.' : undefined} />
        <Text size="xs" c="dimmed">
          At least {MIN_PASSWORD} characters. A suggested one is 125 random bits.
        </Text>
      </Stack>

      <Stack gap={6}>
        <Text fw={600} size="sm">
          2. Your recovery code
        </Text>
        <Text size="sm" c="dimmed">
          If the password is ever forgotten, this code is the only way back in. Save it somewhere that is not this computer.
        </Text>
        <Code block fz="md" style={{ letterSpacing: 1 }}>
          {code}
        </Code>
        <Checkbox label="Put my finance password in the saved file too" checked={includePassword} onChange={(event) => setIncludePassword(event.currentTarget.checked)} disabled={!ready} />
        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconBrandGoogleDrive size={16} />}
            disabled={!ready || !signIn.data?.google_client_id}
            onClick={async () => {
              try {
                await saveToDrive(signIn.data!.google_client_id!, 'Finance recovery - Oro Media Lab.txt', file)
                setSavedTo('Saved to your Google Drive as "Finance recovery - Oro Media Lab.txt".')
                setSaved(true)
              } catch (failed) {
                setSavedTo(failed instanceof Error ? failed.message : 'Google Drive did not take it. Download it and upload it to Drive instead.')
              }
            }}
          >
            Save to Google Drive
          </Button>
          <Button variant="default" leftSection={<IconDownload size={16} />} disabled={!ready} onClick={() => download('Finance recovery - Oro Media Lab.txt', file)}>
            Download
          </Button>
          <CopyButton value={code}>
            {({ copied, copy }) => (
              <Button variant="default" leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} onClick={copy}>
                {copied ? 'Copied' : 'Copy code'}
              </Button>
            )}
          </CopyButton>
        </Group>
        {savedTo && (
          <Text size="xs" c="dimmed">
            {savedTo}
          </Text>
        )}
        <Checkbox label="I have saved the recovery code somewhere other than this computer" checked={saved} onChange={(event) => setSaved(event.currentTarget.checked)} />
      </Stack>

      {error && failure(new Error(error))}

      <Group justify="flex-end">
        <Button loading={busy} disabled={!ready || !saved} onClick={() => void finish()}>
          Set up finance
        </Button>
      </Group>
    </Panel>
  )
}

function recoveryText(code: string, password: string | null): string {
  return [
    'Finance recovery - Oro Media Lab',
    '',
    `Recovery code: ${code}`,
    ...(password ? [`Finance password: ${password}`] : []),
    '',
    'The recovery code opens finance if the password is forgotten: Accounts > Unlock finance > Forgot it? Use the recovery code.',
    'Anybody holding this file can read the company finance. Keep it private.',
    '',
    `Saved ${new Date().toLocaleString('en-IN')}`,
  ].join('\n')
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

type TokenResponse = { access_token?: string; error?: string }
type GoogleIdentity = { accounts: { oauth2: { initTokenClient: (config: { client_id: string; scope: string; callback: (response: TokenResponse) => void; error_callback?: (error: { message?: string }) => void }) => { requestAccessToken: () => void } } } }

/**
 * Straight from this browser to the person's own Drive, with the narrowest
 * scope Google has - files this app creates, nothing else of theirs. The
 * server never sees the file or the token.
 */
async function saveToDrive(clientId: string, name: string, text: string): Promise<void> {
  const google = await loadGoogleIdentity()
  const token = await new Promise<string>((resolve, reject) => {
    google.accounts.oauth2
      .initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: (response) => (response.access_token ? resolve(response.access_token) : reject(new Error('Google Drive was not allowed.'))),
        error_callback: (failed) => reject(new Error(failed.message ?? 'Google Drive was not allowed.')),
      })
      .requestAccessToken()
  })

  const body = new FormData()
  body.append('metadata', new Blob([JSON.stringify({ name, mimeType: 'text/plain' })], { type: 'application/json' }))
  body.append('file', new Blob([text], { type: 'text/plain' }))

  const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body })
  if (!response.ok) throw new Error('Google Drive did not take it. Download it and upload it to Drive instead.')
}

function loadGoogleIdentity(): Promise<GoogleIdentity> {
  const existing = (window as unknown as { google?: GoogleIdentity }).google
  if (existing?.accounts?.oauth2) return Promise.resolve(existing)

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => resolve((window as unknown as { google: GoogleIdentity }).google)
    script.onerror = () => reject(new Error('Google could not be reached. Download the file instead.'))
    document.head.appendChild(script)
  })
}

function Panel({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <Card withBorder radius="md" padding="lg" maw={560} mx="auto" mt="lg">
      <Stack gap="md">
        <Group gap={8}>
          {icon}
          <Title order={4}>{title}</Title>
        </Group>
        {children}
      </Stack>
    </Card>
  )
}

function failure(error: unknown) {
  return (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
      {error instanceof ApiError || error instanceof Error ? error.message : 'Something went wrong.'}
    </Alert>
  )
}
